
import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLeads } from "@/context/LeadContext";
import { usePartners } from "@/context/PartnerContext";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FileBarChart2,
  Calendar,
  TrendingUp,
  Users,
  Target,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Mail,
  PhoneCall,
  Clock,
} from "lucide-react";
import { isToday, parseISO } from "date-fns";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { Badge } from "@/components/ui/badge";

const Reports = () => {
  const { leads } = useLeads();
  const { partners } = usePartners();
  const [timeRange, setTimeRange] = useState("all");

  const filteredLeads = useMemo(() => {
    if (timeRange === "all") return leads;

    const now = new Date();
    const cutoffDate = new Date();

    switch (timeRange) {
      case "week":
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case "month":
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case "quarter":
        cutoffDate.setMonth(now.getMonth() - 3);
        break;
      default:
        return leads;
    }

    return leads.filter(lead => new Date(lead.createdAt) >= cutoffDate);
  }, [leads, timeRange]);

  const stageData = useMemo(() => [
    {
      name: "Νέα",
      value: filteredLeads.filter(lead => lead.stage === "Νέο").length,
      color: "#3b82f6"
    },
    {
      name: "Σε εκκρεμότητα",
      value: filteredLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length,
      color: "#f59e0b"
    },
    {
      name: "Επιτυχία",
      value: filteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length,
      color: "#10b981"
    },
    {
      name: "Πληροφορίες",
      value: filteredLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length,
      color: "#6366f1"
    },
  ], [filteredLeads]);

  const serviceData = useMemo(() => [
    { name: "Εξοικονομώ", value: filteredLeads.filter(lead => lead.service === "εξοικονομώ").length, fill: "#3b82f6" },
    { name: "Αντλίες", value: filteredLeads.filter(lead => lead.service === "αντλίεςΘερμότητας").length, fill: "#8b5cf6" },
    { name: "Φωτοβολταϊκά", value: filteredLeads.filter(lead => lead.service === "φωτοβολταϊκά").length, fill: "#10b981" },
    { name: "Κλιματιστικά", value: filteredLeads.filter(lead => lead.service === "κλιματιστικά").length, fill: "#f59e0b" },
    { name: "Φυσικό Αέριο", value: filteredLeads.filter(lead => lead.service === "φυσικόΑέριο").length, fill: "#ef4444" },
    { name: "Ηλιακά", value: filteredLeads.filter(lead => lead.service === "ηλιακά").length, fill: "#14b8a6" },
    { name: "Άλλο", value: filteredLeads.filter(lead => lead.service === "άλλο").length, fill: "#6b7280" },
  ].sort((a, b) => b.value - a.value), [filteredLeads]);

  const partnerData = useMemo(() => {
    return partners.map(partner => {
      const partnerLeads = filteredLeads.filter(lead => lead.partnerId === partner.id);
      const closedLeads = partnerLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;

      return {
        name: partner.name.length > 15 ? partner.name.substring(0, 15) + '...' : partner.name,
        leads: partnerLeads.length,
        success: closedLeads,
        rate: partnerLeads.length > 0 ? Math.round((closedLeads / partnerLeads.length) * 100) : 0,
      };
    }).sort((a, b) => b.leads - a.leads).slice(0, 8);
  }, [filteredLeads, partners]);

  const metrics = useMemo(() => {
    const total = filteredLeads.length;
    const success = filteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;
    const pending = filteredLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length;
    const newLeads = filteredLeads.filter(lead => lead.stage === "Νέο").length;
    const unassigned = filteredLeads.filter(lead => !lead.partnerId).length;
    const smsSent = filteredLeads.filter(lead => lead.smsStatus === "sent" || lead.smsStatus === "delivered").length;
    const failedSms = filteredLeads.filter(lead => lead.smsStatus === "failed").length;
    const viewed = filteredLeads.filter(lead => lead.viewed).length;
    const todayLeads = filteredLeads.filter(lead => isToday(parseISO(lead.createdAt))).length;

    const stalePending = filteredLeads.filter(lead => {
      if (lead.stage !== "σεΕκκρεμότητα") return false;
      const ageHours = (Date.now() - parseISO(lead.createdAt).getTime()) / (1000 * 60 * 60);
      return ageHours >= 48;
    }).length;

    return {
      total,
      success,
      pending,
      newLeads,
      unassigned,
      smsSent,
      failedSms,
      viewed,
      todayLeads,
      stalePending,
      successRate: total > 0 ? Math.round((success / total) * 100) : 0,
      assignmentRate: total > 0 ? Math.round(((total - unassigned) / total) * 100) : 0,
      viewRate: smsSent > 0 ? Math.round((viewed / smsSent) * 100) : 0,
    };
  }, [filteredLeads]);

  const timeRangeOptions = [
    { value: "all", label: "Όλα τα δεδομένα" },
    { value: "week", label: "Τελευταία εβδομάδα" },
    { value: "month", label: "Τελευταίος μήνας" },
    { value: "quarter", label: "Τελευταίο τρίμηνο" },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Αναφορές"
        description="Ενιαία εικόνα απόδοσης και επιχειρησιακών ενεργειών"
        icon={<FileBarChart2 />}
        iconGradient="orange"
        actions={
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-52 bg-white border-slate-200">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Περίοδος" />
            </SelectTrigger>
            <SelectContent>
              {timeRangeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard value={metrics.total}                  title="Σύνολο Leads"    icon={<Target />}      variant="info"                                                     size="compact" />
        <StatCard value={`${metrics.successRate}%`}      title="Success Rate"    icon={<TrendingUp />}  variant="success"                                                  size="compact" />
        <StatCard value={`${metrics.assignmentRate}%`}   title="Assignment Rate" icon={<Users />}       variant="gradient" gradientColors="from-violet-500 to-purple-600" size="compact" />
        <StatCard value={metrics.todayLeads}             title="Σήμερα"          icon={<Clock />}       variant="warning"                                                  size="compact" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Προτεραιότητες</CardTitle>
            <CardDescription>Σημεία που θέλουν άμεση ενέργεια</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-700">Leads χωρίς ανάθεση</p>
              <p className="text-2xl font-semibold text-amber-800 mt-1">{metrics.unassigned}</p>
            </div>
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
              <p className="text-xs text-orange-700">Εκκρεμότητα 48h+</p>
              <p className="text-2xl font-semibold text-orange-800 mt-1">{metrics.stalePending}</p>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="text-xs text-red-700">Αποτυχίες SMS</p>
              <p className="text-2xl font-semibold text-red-800 mt-1">{metrics.failedSms}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Quick Links</CardTitle>
            <CardDescription>Εργαλεία δράσης</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {[
                { to: "/admin/leads",              label: "Leads Queue",     icon: Target,      color: "text-blue-600",    bg: "bg-blue-50" },
                { to: "/admin/leads/statistics",   label: "Lead Stats",      icon: TrendingUp,  color: "text-emerald-600", bg: "bg-emerald-50" },
                { to: "/admin/messages/statistics",label: "SMS Stats",       icon: PhoneCall,   color: "text-violet-600",  bg: "bg-violet-50" },
                { to: "/admin/email/statistics",   label: "Email Stats",     icon: Mail,        color: "text-orange-600",  bg: "bg-orange-50" },
              ].map(({ to, label, icon: Icon, color, bg }) => (
                <Link key={to} to={to}>
                  <div className="group flex flex-col items-center gap-2 rounded-xl border border-slate-100 bg-white p-3 text-center hover:border-slate-200 hover:shadow-sm transition-all cursor-pointer">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${bg} group-hover:scale-110 transition-transform`}>
                      <Icon className={`h-4 w-4 ${color}`} />
                    </div>
                    <p className="text-xs font-semibold text-slate-700 leading-tight">{label}</p>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-50">
                <Target className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Funnel Σταδίων</CardTitle>
                <CardDescription>Κατανομή leads ανά status</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stageData}
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={95}
                  paddingAngle={4}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: '#94a3b8' }}
                >
                  {stageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [value, name]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <FileBarChart2 className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Ζήτηση ανά Υπηρεσία</CardTitle>
                <CardDescription>Ποια υπηρεσία φέρνει περισσότερα leads</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serviceData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={85} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Bar dataKey="value" name="Επαφές" radius={[0, 4, 4, 0]}>
                  {serviceData.map((entry) => (
                    <Cell key={`service-${entry.name}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-50">
                <Users className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Απόδοση Συνεργατών</CardTitle>
                <CardDescription>Σύγκριση leads και επιτυχιών</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={partnerData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Legend />
                <Bar dataKey="leads" name="Leads" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="success" name="Επιτυχίες" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Υγεία Καναλιών</CardTitle>
            <CardDescription>SMS και engagement</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* SMS Sent */}
            <div className="flex items-center justify-between border-l-4 border-l-blue-400 bg-blue-50 rounded-r-lg px-3 py-2">
              <span className="text-sm text-slate-700">SMS Sent</span>
              <span className="font-bold text-blue-700">{metrics.smsSent}</span>
            </div>
            {/* View Rate with bar */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-slate-600">View Rate</span>
                <span className={`text-sm font-bold ${metrics.viewRate >= 50 ? 'text-emerald-600' : metrics.viewRate >= 25 ? 'text-amber-600' : 'text-rose-600'}`}>
                  {metrics.viewRate}%
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full transition-all ${metrics.viewRate >= 50 ? 'bg-emerald-500' : metrics.viewRate >= 25 ? 'bg-amber-500' : 'bg-rose-500'}`}
                  style={{ width: `${metrics.viewRate}%` }}
                />
              </div>
            </div>
            {/* Pending */}
            <div className={`flex items-center justify-between border-l-4 rounded-r-lg px-3 py-2 ${metrics.pending > 0 ? 'border-l-amber-400 bg-amber-50' : 'border-l-slate-200 bg-slate-50'}`}>
              <span className="text-sm text-slate-700">Leads Εκκρεμότητα</span>
              <span className={`font-bold ${metrics.pending > 0 ? 'text-amber-700' : 'text-slate-700'}`}>{metrics.pending}</span>
            </div>
            {/* Alert badges */}
            <div className="flex flex-wrap gap-2 pt-1">
              {metrics.failedSms > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> {metrics.failedSms} SMS failures
                </Badge>
              )}
              {metrics.newLeads > 0 && (
                <Badge variant="outline" className="gap-1 text-blue-700 border-blue-200 bg-blue-50">
                  <CheckCircle className="h-3.5 w-3.5" /> {metrics.newLeads} νέα leads
                </Badge>
              )}
              {metrics.unassigned > 0 && (
                <Badge variant="outline" className="gap-1 text-amber-700 border-amber-200 bg-amber-50">
                  <AlertTriangle className="h-3.5 w-3.5" /> {metrics.unassigned} unassigned
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
